import React, { useEffect, useState } from 'react';
import { MenuProps, Select, Spin } from 'antd';
import { Button, Icon } from 'Components';
import { useStores } from 'Hooks/useStore';
import { observer } from 'mobx-react-lite';
import { SystemRoleType } from 'Services/Api/Roles/Types';
import {
	discardPhishingCampaign,
	fetchCampaignReviewData,
	getPhishingCampaignData,
	getPhishingCampaignsForTenant,
	releasePhishingCampaign,
	reruncampaign
} from './service/index.Service';
import CampaignSetup from './pages/CampaignSteps';
import CampaignLimitWarningModal from './pages/Phishing/CampaignLimitWarningModal';
import DiscardCampaignModal from './pages/Phishing/DiscardCampaignModal';
import CampaignList from './pages/Phishing/CampaignList';
import EmptyState from './components/EmptyState';
import CampaignLimitToast from './components/Toast';
import {
	DEFAULT_CAMPAIGNS_PAGESIZE,
	DEFAULT_WHITELIST_URL
} from './PhishingManager.constants';
import Header from './components/Header';
import Alert from './components/Alert';
import { useIsMobile } from 'Hooks';
import InfinityScrollWrapper from 'Components/InfinityScroll/InfinityScrollWrapper';
import TestCampaignModal from './pages/Phishing/TestCampaignModal';
import CampaignErrorModal from './pages/Phishing/CampaignError';
import ReRunModal from './pages/Phishing/ReRunModal';
import ReleaseCampaignModal from './pages/Phishing/ReleaseFullCampaignModal';
import { errorMessageHandler } from './utils';
import { CAMPAIGN_DATA } from './pages/CampaignReport/CampaignReport.type';
import { useHistory } from 'react-router-dom';
import { getAppRoutes } from 'Pages/App/App.constants';
import PaginationElement from './components/Pagination';
import { CampaignReviewData } from './pages/Step5/Step5.types';

type PHISHING_PROPS = {
	updateCampaignData: (data: CAMPAIGN_DATA) => void;
}

export interface CampaignData {
	id: number | string;
	name: string;
	status: string;
	campaign_type: string;
	schedule_datetime: string | null;
	schedule_timezone: string | null;
	campaign_duration: number;
	image_url: string;
}

export interface CampaignThresholdData {
	campaign_count: number;
	first_campaign_created_at: string;
	tenant_campaigns_threshold: number;
	current_period_started_at: string;
	threshold_period_months: number;
	is_threshold_reached: boolean;
}
type MenuClickInfo = Parameters<NonNullable<MenuProps['onClick']>>[0];
type CampaignCompletePopup = 'success' | 'error' | null;

const toastInitialValue: { message: string; type: "warning" } = { message: '', type: "warning" }

const PhishingManager: React.FC<PHISHING_PROPS> = ({ updateCampaignData }) => {
	const [
		campaignThresholdData,
		setCampaignThresholdData
	] = useState<CampaignThresholdData | null>(null);
	const [toast, setToast] = useState<{
		message: string;
		type: 'warning' | 'success';
	}>({ ...toastInitialValue });
	const [selectedTenantId, setSelectedTenantId] = useState<
		string | number | undefined
	>(undefined);
	const [isLoading, setIsLoading] = useState(false);
	const [phishingArray, setPhishingArray] = useState<CampaignData[]>([]);
	const [itemToDiscard, setItemToDiscard] = useState<string | null>(null);
	const [warningModal, setWarningModal] = useState(false);
	const [newCampaignSetup, setNewCampaignSetup] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const [totalItems, setTotalItems] = useState(0);
	const [pageSize, setPageSize] = useState(DEFAULT_CAMPAIGNS_PAGESIZE);
	const [reRunId, setReRunId] = useState<string | null>(null);
	const [viewSetupSummary, setViewSetupSummary] = useState<{ id: number, name: string, data: CampaignReviewData } | null>(null);
	const [releaseCampaign, setReleaseCampaign] = useState<{ id: number, userCount: number } | null>(null);
	const [
		campaignCompletePopup,
		setCampaignCompletePopup
	] = useState<CampaignCompletePopup>(null);
	const [testEmailsCount, setTestEmailsCount] = useState(0);
	const threshold = campaignThresholdData?.tenant_campaigns_threshold ?? 0;
	const campaignCount = campaignThresholdData?.campaign_count ?? 0;
	const isMobile = useIsMobile();
	const history = useHistory();
	const { authStore, systemsStore } = useStores();
	const { tenantList: TENANTS, selectedTenant, tenantLoading } = systemsStore;

	const handleMenuClick = (item: CampaignData) => (info: MenuClickInfo) => {
		const itemId = String(item.id);
		if (info.key === 'view-setup-summary') getReviewData(Number(itemId), item.name);
		else if (info.key === 'discard') setItemToDiscard(itemId);
		else if (info.key === 'release') setReleaseCampaign({ id: Number(itemId), userCount: 1500 });
		else if (info.key === 'rerun') setReRunId(itemId);
		else if (info.key === 'report') {
			updateCampaignData({ id: Number(itemId), tenentId: Number(selectedTenantId)});
			history.push(getAppRoutes().PHISHING);
		}
	};

	const confirmDiscard = async () => {
		const deletingId = itemToDiscard;
		setItemToDiscard(null);
		setIsLoading(true);
		try {
			const result = await discardPhishingCampaign({
				venza_tenant_id: Number(selectedTenantId),
				campaign_id: Number(deletingId)
			});
			setToast({ message: result.message, type: 'success' });
			const isLastItemOnPage = phishingArray.length === 1;
			let nextPage = currentPage;
			if (isLastItemOnPage && currentPage > 1) {
				nextPage = currentPage - 1;
			}
			fetchCampaigns(selectedTenantId, nextPage);
		} catch (err: any) {
			const toastMessage = errorMessageHandler(
				err,
				'Failed to discard campaign'
			);
			setToast({ message: toastMessage, type: 'warning' });
		} finally {
			setIsLoading(false);
		}
	};

	const confirmReReleaseModal = async (type: "release" | "re-run") => {
		setIsLoading(true);
		try {
			let result = null;
			if (type === "re-run") {
				setReRunId(null);
				result = await reruncampaign({
					venza_tenant_id: Number(selectedTenantId),
					campaign_id: Number(reRunId)
				});
			} else {
				setReleaseCampaign(null);
				const id = releaseCampaign?.id || 0;
				result = await releasePhishingCampaign({
					venza_tenant_id: Number(selectedTenantId),
					campaign_id: id
				});
			}
			setToast({ message: result?.message, type: 'success' });
			fetchCampaigns(selectedTenantId, currentPage);
		} catch (err: any) {
			const toastMessage = errorMessageHandler(
				err,
				'Failed to release campaign'
			);
			setToast({ message: toastMessage, type: 'warning' });
		} finally {
			setIsLoading(false);
			setReleaseCampaign(null);
		}
	};

	const getReviewData = async (campaignId: number, campaignName: string) => {
		if (!selectedTenantId || !campaignId) return;
		setIsLoading(true);
		const { data, error } = await fetchCampaignReviewData(selectedTenantId, campaignId);
		if (error) setToast({ message: error, type: "warning" });
		setViewSetupSummary(data ? { id: campaignId, name: campaignName, data } : null)
		setIsLoading(false);
	}

	const confirmWarningDiscard = () => {
		newCampaign();
		setWarningModal(false);
	};

	const closeModal = () => {
		setItemToDiscard(null);
	};

	const closeWarningModal = () => {
		setWarningModal(false);
	};

	const closeReRunModal = () => setReRunId(null);

	const toggleCampaignCompletePopUp = (value: CampaignCompletePopup) => {
		setCampaignCompletePopup(value);
	};

	const confirmCampaignError = () => {
		newCampaign();
		toggleCampaignCompletePopUp(null);
	};

	const handleCreateCampaign = () => {
		if (campaignCount === threshold - 1) {
			setWarningModal(true);
		} else if (
			!!campaignThresholdData?.is_threshold_reached &&
			campaignCount >= threshold
		) {
			setToast({
				message: 'Campaign limit reached. No more campaigns can be created.',
				type: 'warning'
			});
		} else {
			if (!!campaignThresholdData) {
				newCampaign();
			} else {
				if (!!toast?.message) setToast({ message: toast.message, type: 'warning' });
			}
		}
	};

	const newCampaign = () => {
		setNewCampaignSetup(true);
	};

	const closeReReleaseModal = () => {
		setReleaseCampaign(null);
	};

	const closeTestCampaignModal = () => {
		setTestEmailsCount(0);
		toggleCampaignCompletePopUp(null);
	}

	const isSuperAdmin =
		authStore.currentUser?.primarySystemRole?.role ===
		SystemRoleType.SuperAdmin;

	const handleTenantChange = async (id: number | string) => {
		const tenant = TENANTS.find(tenant => tenant.value === id);
		if(tenant) systemsStore.changeTenant({ label: tenant.label, value: tenant.value });
	};

	const handleTenantDataChange = async (id: string | number) => {
		setIsLoading(true);
		setCurrentPage(1);
		await fetchCampaigns(id);
		await fetchCampaignsData(String(id));
		setSelectedTenantId(id);
	};

	const fetchCampaigns = async (
		value: string | number | undefined,
		pagination?: number
	) => {
		try {
			setIsLoading(true);
			const page = pagination ?? 1;
			const response = await getPhishingCampaignsForTenant(value, page, DEFAULT_CAMPAIGNS_PAGESIZE);
			const { current_page, per_page, total } = response?.meta || {};
			if (response?.meta) {
				setTotalItems(total);
				setPageSize(per_page);
				setCurrentPage(current_page);
			}
			const campaignData =
				response?.data.map((el: CampaignData) => {
					let formattedStatus = '';
					switch (el.status) {
						case 'draft':
							formattedStatus = 'Draft';
							break;
						case 'in_testing':
							formattedStatus = 'Testing';
							break;
						case 'completed':
							formattedStatus = 'Complete';
							break;
						case 'scheduled':
							formattedStatus = 'Scheduled';
							break;
						case 'in_progress':
							formattedStatus = 'Live';
							break;
						case 'canceled':
							formattedStatus = 'Cancelled';
							break;
						case 'failed':
							formattedStatus = 'Failed';
							break;
						default:
							formattedStatus = el.status;
					}
					return { ...el, status: formattedStatus };
				}) || [];
			setPhishingArray((prev) => {
				if (!isMobile || current_page === 1) return campaignData;
				return [...prev, ...campaignData];
			});
		} catch (error) {
			setPhishingArray([]);
			console.error('Failed to fetch campaigns:', error);
		} finally {
			setIsLoading(false);
		}
	};

	const fetchCampaignsData = async (value: string) => {
		setIsLoading(true);
		try {
			const response = await getPhishingCampaignData(value);
			setCampaignThresholdData(response.data);
			if (response.data?.is_threshold_reached) {
				setToast({
					message:
						'Campaign limit reached. No more campaigns can be created.',
					type: 'warning'
				});
			} else {
				setToast({ ...toastInitialValue });
			}
		} catch (error: any) {
			setCampaignThresholdData(null);
			const toastMessage = errorMessageHandler(
				error,
				'Failed to launch campaign'
			);
			setToast({ message: toastMessage, type: 'warning' });
			console.error('Failed to fetch campaigns:', toastMessage);
		} finally {
			setIsLoading(false);
		}
	};

	const handleCampaignSetupBack = () => {
		setNewCampaignSetup(false);
		setViewSetupSummary(null);
		fetchCampaigns(selectedTenantId);
	};

	useEffect(() => {
		if (!toast?.message) return;
		const timer = setTimeout(() => {
			setToast({ ...toastInitialValue });
		}, 3000);
		return () => clearTimeout(timer);
	}, [toast]);

	useEffect(() => {
		const newTenantId = selectedTenant.value;
		if(newTenantId) handleTenantDataChange(newTenantId);
	}, [selectedTenant])

	return (
		<Spin spinning={isLoading}>
			<div>
				<CampaignLimitToast
					visible={!!toast.message}
					message={toast?.message ?? ''}
					type={toast?.type ?? 'warning'}
					onClose={() => setToast({ ...toastInitialValue })}
				/>
				{(newCampaignSetup || viewSetupSummary) ? (
					<CampaignSetup
						{...{ isMobile, setToast, toggleCampaignCompletePopUp, viewSetupSummary }}
						thresholdData={campaignThresholdData}
						tenantId={selectedTenantId}
						onBack={handleCampaignSetupBack}
						setEmailCount={(count: number) => setTestEmailsCount(count)}
					/>
				) : (
					<>
						<Header
							title="Phishing Manager"
							description="Create, monitor, and learn from phishing simulations"
							parentClass='phishing-header'
						>
							<div className="pm-dropdown-section">
								<label
									htmlFor="pm-client-select"
									className="pm-client-label"
								>
									Tenant
								</label>
								<Select
									id="pm-client-select"
									placeholder="Select a Tenant"
									dropdownClassName="campaign-setup-filter-option-select phishing-tenant-dropdown"
									value={selectedTenantId}
									onChange={handleTenantChange}
									showSearch
									disabled={!isSuperAdmin || tenantLoading}
									optionFilterProp="label"
									suffixIcon={<Icon type="campaignDropdown" />}
									options={TENANTS}
									getPopupContainer={(trigger) => trigger.parentElement}
								/>
							</div>

							<Button
								shape="round"
								type="primary"
								className={`cta-prominent ${campaignCount >= threshold
									? !selectedTenantId || !!toast?.message
										? 'cta-disabled'
										: 'cta-disabled'
									: ''
									}`}
								onClick={handleCreateCampaign}
								disabled={
									campaignCount >= threshold
										? !selectedTenantId || !!toast?.message
											? true
											: true
										: false
								}
							>
								<Icon type="campaignAdd" />
								New Campaign
							</Button>
						</Header>

						{phishingArray.length === 0 ? (
							!selectedTenantId ? (
								<EmptyState
									title="No tenants selected yet"
									description={[
										"You haven't selected a tenant yet.",
										"Start by selecting a tenant to create a phishing campaign."
									]}
									actionLabel={null}
								/>
							) : (
								<EmptyState
									title="No campaigns yet"
									description={[
										"You haven’t created any phishing campaigns yet.",
										"Start by creating a campaign to simulate phishing attacks and measure how your users respond."
									]}
									onAction={handleCreateCampaign}
								/>
							)
						) : (
							<div className="pm-campaign-container">
								<div className="pm-campaigns-header">
									Campaigns
								</div>
								{isMobile ? (
									<InfinityScrollWrapper
										dataLength={phishingArray.length}
										hasMore={
											phishingArray.length < totalItems
										}
										onLoadMore={() =>
											fetchCampaigns(
												selectedTenantId,
												currentPage + 1
											)
										}
										height={1000}
										scrollableId="campaign-scroll-container"
										loaderText={'Loading more campaigns...'}
										endMessageText={'All campaigns loaded'}
									>
										<CampaignList
											campaigns={phishingArray}
											onMenuClick={handleMenuClick}
										/>
									</InfinityScrollWrapper>
								) : (
									<CampaignList
										campaigns={phishingArray}
										onMenuClick={handleMenuClick}
									/>
								)}

								{!isMobile && totalItems / pageSize > 1 && (<PaginationElement
									total={totalItems}
									pageSize={pageSize}
									current={currentPage}
									onChange={(page) => {
										setCurrentPage(page);
										fetchCampaigns(selectedTenantId, page);
									}} />
								)}
							</div>
						)}
						<Alert
							title="Configure Whitelisting for Successful Campaigns"
							subtitle="To ensure phishing simulations are delivered, clients must whitelist Venza’s email infrastructure. This step allows campaigns to be accessible and prevents client-side security controls from blocking email delivery."
							btnText="View Whitelisting Guide"
							btnClick={() =>
								window.open(DEFAULT_WHITELIST_URL, '_blank')
							}
						/>
					</>
				)}
				<CampaignLimitWarningModal
					open={warningModal}
					onClose={closeWarningModal}
					onConfirm={confirmWarningDiscard}
				/>
				<DiscardCampaignModal
					open={!!itemToDiscard}
					onClose={closeModal}
					onConfirm={confirmDiscard}
				/>
				<TestCampaignModal
					testEmailsCount={testEmailsCount}
					open={campaignCompletePopup == 'success'}
					onClose={closeTestCampaignModal}
					onConfirm={closeTestCampaignModal}
				/>
				<CampaignErrorModal
					open={campaignCompletePopup == 'error'}
					onClose={() => toggleCampaignCompletePopUp(null)}
					onConfirm={confirmCampaignError}
				/>
				{reRunId != null && (
					<ReRunModal
						open={!!reRunId}
						onClose={closeReRunModal}
						onConfirm={() => confirmReReleaseModal("re-run")}
					/>
				)}
				{releaseCampaign != null && (
					<ReleaseCampaignModal
						userCount={releaseCampaign.userCount || 0}
						visible={!!releaseCampaign.id}
						onClose={closeReReleaseModal}
						onRelease={() => confirmReReleaseModal("release")}
					/>
				)}
			</div>
		</Spin>
	);
};

export default observer(PhishingManager);